/* eslint-disable camelcase */
import React, { useState } from 'react';
import {
  useBlocker,
  useNavigate,
} from 'react-router';
import {
  Formik,
  Field,
} from 'formik';
import {
  useDispatch,
} from 'react-redux';

import {
  coreActions,
} from '@gymcloud/core';

import {
  commonActions,
} from 'web-actions';

import {
  Video,
  Button,
  Author,
  TagList,
  TagField,
  TextField,
  EditorField,
  MarkdownRenderer,
} from 'web-components';

import {
  Flex,
} from 'web-styled';

import * as S from './styled';

type Props = {
  isPro: boolean,
  readOnly?: boolean,
  program: any,
  basePath: string,
  resourceType: 'programTemplates' | 'personalPrograms',
  onRequestDelete?: { (): void },
};

const ProgramTemplate = ({
  isPro,
  readOnly = false,
  program,
  basePath,
  resourceType,
  onRequestDelete,
}: Props) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [shouldBlockNavigation, setShouldBlockNavigation] = useState(false);

  // Block navigating elsewhere when data has been entered into the input
  const blocker = useBlocker(({ currentLocation, nextLocation }) => (
    shouldBlockNavigation && currentLocation.pathname !== nextLocation.pathname));

  if (blocker.state === 'blocked') {
    const ask = window.confirm('Are you sure you want to leave without saving?');

    if (ask) {
      blocker.proceed();
    } else {
      blocker.reset();
    }
  }

  const goBack = () => {
    navigate(`${basePath}/${program.id}/overview`);
  };

  const handleUpdate = (payload, actions) => {
    setShouldBlockNavigation(false);
    dispatch(
      coreActions.requestUpdateEntity({
        id: program.id,
        payload,
        endNotify: true,
        resourceType,
        callbacks: {
          ...actions,
          onSuccess: goBack,
        },
      }),
    );
  };

  return (
    <Formik
      initialValues={{
        name: program?.name || '',
        tag_list: program?.tag_list ? program.tag_list.join(',') : '',
        video_id: program?.video_id || '',
        video_url: program?.video_url || '',
        description: program?.description || '',
      }}
      onSubmit={handleUpdate}
    >
      {(formikProps) => (
        <Flex
          fillSpace
          column
          as="form"
          onKeyPress={() => {
            setShouldBlockNavigation(true);
          }}
        >
          <S.Header>
            <Author
              fullName={program?.author_full_name}
            />
            <Flex spacing={15}>
              {isPro && (
                <Button
                  ghost
                  appearance="primary"
                  onClick={() => {
                    if (readOnly) {
                      navigate(`${basePath}/${program.id}/edit`);
                    } else if (formikProps.dirty) {
                      formikProps.resetForm({ values: formikProps.values });
                      formikProps.submitForm();
                    } else {
                      goBack();
                    }
                  }}
                >
                  {readOnly ? 'Edit' : 'Done'}
                </Button>
              )}

              {(resourceType === 'personalPrograms' && readOnly) && (
                <>
                  <Button
                    ghost
                    appearance="primary"
                    onClick={() => {
                      window.print();
                      dispatch(commonActions.triggerAnalytic('clicked_print_button'));
                    }}
                  >
                    Print
                  </Button>
                  {isPro && (
                    <Button
                      ghost
                      appearance="danger"
                      onClick={onRequestDelete}
                    >
                      Delete
                    </Button>
                  )}
                </>
              )}
            </Flex>
          </S.Header>
          <Flex
            fillSpace
            noScroll
            spacing={30}
            padding="30px"
          >
            <Flex
              column
              width="45%"
              style={{
                minWidth: '450px',
              }}
              noPrint
            >
              <Video
                {...(readOnly
                  ? {
                    id: program.video_id,
                    src: program.video_url,
                    readOnly: true,
                  } : {
                    id: formikProps.values.video_id,
                    src: formikProps.values.video_url,
                  }
                )}
                title={program.name}
                height="350px"
                entityId={program.id}
                resourceType={resourceType}
                onAssign={(video) => {
                  setShouldBlockNavigation(true);
                  const id = video.embed_url.indexOf('vimeo') !== -1 ? video.id : null;
                  formikProps.setFieldValue('video_id', id);
                  formikProps.setFieldValue('video_url', video.embed_url);
                }}
                onUploaded={(videos) => {
                  setShouldBlockNavigation(true);
                  formikProps.setFieldValue('video_id', videos[0].id);
                  formikProps.setFieldValue('video_url', '');
                }}
                onDelete={() => {
                  setShouldBlockNavigation(true);
                  formikProps.setFieldValue('video_id', '');
                  formikProps.setFieldValue('video_url', '');
                }}
              />
            </Flex>
            {readOnly ? (
              <Flex
                column
                spacing={30}
                padding="0 0 30px 0"
              >
                <S.Title>
                  {program.name}
                </S.Title>

                <MarkdownRenderer
                  source={program?.description}
                  maxHeight={180}
                  placeholder={isPro
                    ? 'Click the Edit button to add a program description'
                    : null}
                />

                <TagList
                  tags={program?.tag_list}
                  resourceType="programs"
                />
              </Flex>
            ) : (
              <Flex
                column
                width="65%"
                spacing={30}
                padding="0 0 30px 0"
              >
                <Field
                  flex="0 0 60px"
                  name="name"
                  label="Name"
                  color="primary"
                  component={TextField}
                />
                <Field
                  rows={6}
                  name="description"
                  label="Description"
                  color="primary"
                  placeholder="Type program description here..."
                  component={EditorField}
                />
                <Field
                  name="tag_list"
                  component={TagField}
                />
              </Flex>
            )}
          </Flex>
        </Flex>
      )}
    </Formik>
  );
};

export default ProgramTemplate;
